import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Dropdown from 'react-bootstrap/Dropdown';
import { useDispatch } from 'react-redux';
import ErrorBanner from '@avtechsoftware/react-component-library/src/components/common/ErrorBanner';
import AppDropdown from '../../../AppDropdown/AppDropdown';
import AdvancedSearchListButtonGroup from './AdvancedSearchListButtonGroup/AdvancedSearchListButtonGroup';
import savedSearchPropType from '../../../../../utility/PropTypes/savedSearchPropType';
import { connectModalContainerContextToComponent } from '../../../../../contexts/ModalContext/ModalContext';
import SaveNewSavedSearchModal from './AdvancedSearchListButtonGroup/SaveNewSavedSearchModal/SaveNewSavedSearchModal';
import { editSavedSearch } from '../../../../../redux/slices/savedSearchesSlice/savedSearchesSlice';
import searchFilterPropType from '../../../../../utility/PropTypes/searchFilterPropType';
import HasRolePrivileges from '../../../../highorder/HasRolePrivileges';
import { RaaRoles } from '../../../../../utility/SecurityRules';
import SearchType from '../../../../../utility/types/SearchType';
import { setAppliedAlertSearch } from '../../../../../redux/slices/alertListSlice/alertListSlice';
import { setAppliedDeviceSearch } from '../../../../../redux/slices/filteredDeviceListSlice/filteredDeviceListSlice';

function AdvancedListSearchHeader({
  savedSearches,
  activeSavedSearch,
  setActiveSavedSearchToEdit,
  setIsEditingSavedSearch,
  filters,
  setFilters,
  isEditingSavedSearch,
  handleClearSearch,
  handleDiscardSavedSearchChanges,
  entityTypeId,
  fallbackTitle = 'Search',
  setModalJSX,
}) {
  const dispatch = useDispatch();
  const [error, setError] = useState(false);
  const hasSelectedFilters = filters.length > 0;
  const title = activeSavedSearch && activeSavedSearch.id ? (<span>{activeSavedSearch.name}</span>) : (<span className="text-muted">{fallbackTitle}</span>);
  const setAppliedSearch = entityTypeId === SearchType.ALERT
    ? setAppliedAlertSearch : setAppliedDeviceSearch;

  // TODO: feedback for success/fail request res (maybe toast messages?)
  const handleSaveEdited = async () => {
    try {
      await dispatch(editSavedSearch({
        savedSearch: activeSavedSearch, patchFields: ['filters'],
      })).unwrap();
      setIsEditingSavedSearch(false);
    } catch (e) {
      setError(true);
    }
  };

  const handleSaveAs = () => {
    let newSavedSearch;
    if (activeSavedSearch) {
      newSavedSearch = { ...activeSavedSearch };
    } else {
      newSavedSearch = {
        name: '',
        description: '',
        type: entityTypeId,
        filters,
      };
    }
    setModalJSX(
      <SaveNewSavedSearchModal
        newSavedSearch={newSavedSearch}
        setActiveSavedSearchToEdit={setActiveSavedSearchToEdit}
        entityTypeId={entityTypeId}
      />,
    );
  };

  React.useEffect(() => {
    if (activeSavedSearch && !activeSavedSearch.id && activeSavedSearch.filters.length === 0) {
      setIsEditingSavedSearch(false);
      setActiveSavedSearchToEdit(null);
    }
  }, [activeSavedSearch]);

  /* istanbul ignore next */
  return (
    <>
      <div className="clearfix mt-3 mb-2">
        <div className="float-left d-flex flex-row">
          {
            savedSearches.length > 0 && (
              <AppDropdown id="saved-search-dropdown">
                {savedSearches.map((search, index) => (
                  <Dropdown.Item
                    key={search.id}
                    eventKey={(index + 1)}
                    onClick={() => {
                      setFilters(search.filters);
                      setActiveSavedSearchToEdit(search);
                      dispatch(setAppliedSearch(search));
                      setIsEditingSavedSearch(false);
                    }}
                  >
                    {search.name}
                  </Dropdown.Item>
                ))}
                <HasRolePrivileges targetRoleList={[RaaRoles.ADMIN, RaaRoles.MANAGER]}>
                  <hr className="my-1" />
                  <Dropdown.Item
                    eventKey={(savedSearches.length + 2)}
                    href={entityTypeId === SearchType.ALERT ? '/s/alerts/saved-searches' : '/s/devices/saved-searches'}
                  >
                    Manage Searches...
                  </Dropdown.Item>
                </HasRolePrivileges>
              </AppDropdown>
            )
          }
          <h3 className="ml-2 mr-3 mb-0">
            {title}
            {isEditingSavedSearch && <span className="text-muted"><small> - Edited</small></span>}
          </h3>
          {
            (hasSelectedFilters || activeSavedSearch) && (
              <AdvancedSearchListButtonGroup
                handleClearSearch={handleClearSearch}
                handleSaveEdited={handleSaveEdited}
                handleSaveAs={handleSaveAs}
                handleDiscardSavedSearchChanges={handleDiscardSavedSearchChanges}
                isEditingSavedSearch={isEditingSavedSearch}
              />
            )
          }
        </div>
      </div>
      {error && (
        <div className="row">
          <div className="col">
            <ErrorBanner onCloseError={() => setError(false)}>
              Unexpected error editing search.
            </ErrorBanner>
          </div>
        </div>
      )}
    </>
  );
}

AdvancedListSearchHeader.defaultProps = {
  activeSavedSearch: null,
  fallbackTitle: 'Search',
};
AdvancedListSearchHeader.propTypes = {
  savedSearches: PropTypes.arrayOf(
    PropTypes.shape(savedSearchPropType),
  ).isRequired,
  activeSavedSearch: PropTypes.shape(savedSearchPropType),
  filters: PropTypes.arrayOf(PropTypes.shape(searchFilterPropType)).isRequired,
  isEditingSavedSearch: PropTypes.bool.isRequired,
  handleClearSearch: PropTypes.func.isRequired,
  handleDiscardSavedSearchChanges: PropTypes.func.isRequired,
  entityTypeId: PropTypes.number.isRequired,
  setModalJSX: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  setActiveSavedSearchToEdit: PropTypes.func.isRequired,
  setIsEditingSavedSearch: PropTypes.func.isRequired,
  fallbackTitle: PropTypes.string,
};
export { AdvancedListSearchHeader };
export default connectModalContainerContextToComponent(
  AdvancedListSearchHeader,
);
